import { useState } from 'react';
import { App, Card, Button, Space, DatePicker, Select, Tag } from 'antd';
import { DownloadOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import { ResponsiveTable, type ResponsiveColumnType } from '../../../components/common/responsive';
import { PageHeader, PageContainer } from '../../../components/common';
import dayjs from 'dayjs';
const { RangePicker } = DatePicker;

type AuditStatus = 'success' | 'failure' | 'error';

interface AuditLog {
  id: string;
  username: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  ipAddress: string;
  status: string;
  errorMessage: string | null;
  createdAt: Date;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeAuditLog = (value: unknown): AuditLog | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    username: typeof value.username === 'string' ? value.username : null,
    action: typeof value.action === 'string' ? value.action : '',
    resourceType: typeof value.resourceType === 'string' ? value.resourceType : '',
    resourceId: typeof value.resourceId === 'string' ? value.resourceId : null,
    ipAddress: typeof value.ipAddress === 'string' ? value.ipAddress : '',
    status: typeof value.status === 'string' ? value.status : 'success',
    errorMessage: typeof value.errorMessage === 'string' ? value.errorMessage : null,
    createdAt: new Date(typeof value.createdAt === 'string' ? value.createdAt : Date.now()),
  };
};

export function AuditLogViewerPage() {
  const { message } = App.useApp();
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState<{
    action?: string;
    status?: AuditStatus;
    dateFrom?: string;
    dateTo?: string;
  }>({});

  const { data, isLoading, refetch } = trpc.adminAuditLog.search.useQuery({
    page,
    limit,
    ...filters,
  });

  const exportMutation = trpc.adminAuditLog.export.useMutation({
    onSuccess: (result) => {
      const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
      message.success(`${result.count}件のログをエクスポートしました`);
    },
    onError: (error) => {
      message.error(error.message || 'エクスポートに失敗しました');
    },
  });

  const logs = (data?.logs ?? []).flatMap((log) => {
    const normalized = normalizeAuditLog(log);
    return normalized ? [normalized] : [];
  });

  const handleExport = async () => {
    await exportMutation.mutateAsync(filters);
  };

  const columns: ResponsiveColumnType<AuditLog>[] = [
    {
      title: '日時',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: Date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: 'ユーザー',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (username: string | null) => username || <Tag color="default">Unknown</Tag>,
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: 'アクション',
      dataIndex: 'action',
      key: 'action',
      width: 150,
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: 'リソース種別',
      dataIndex: 'resourceType',
      key: 'resourceType',
      width: 120,
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: 'IPアドレス',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
      mobileRole: 'hidden',
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const color = status === 'success' ? 'green' : status === 'failure' ? 'red' : 'orange';
        return <Tag color={color}>{status}</Tag>;
      },
      mobileRole: 'tag',
      mobilePriority: 5,
    },
    {
      title: 'エラーメッセージ',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      ellipsis: true,
      render: (msg: string | null) => msg || '-',
      mobileRole: 'hidden',
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="監査ログビューア"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              更新
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exportMutation.isPending}
            >
              CSVエクスポート
            </Button>
          </Space>
        }
      />

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>

          <Space wrap>
            <Select
              placeholder="アクション"
              style={{ width: 180 }}
              allowClear
              onChange={(value) => setFilters({ ...filters, action: value })}
            >
              <Select.Option value="admin.login">admin.login</Select.Option>
              <Select.Option value="user.create">user.create</Select.Option>
              <Select.Option value="user.update">user.update</Select.Option>
              <Select.Option value="user.delete">user.delete</Select.Option>
              <Select.Option value="role.update">role.update</Select.Option>
            </Select>

            <Select
              placeholder="ステータス"
              style={{ width: 140 }}
              allowClear
              onChange={(value: AuditStatus | undefined) => setFilters({ ...filters, status: value })}
            >
              <Select.Option value="success">成功</Select.Option>
              <Select.Option value="failure">失敗</Select.Option>
              <Select.Option value="error">エラー</Select.Option>
            </Select>

            <RangePicker
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setFilters({
                    ...filters,
                    dateFrom: dates[0].toISOString(),
                    dateTo: dates[1].toISOString(),
                  });
                } else {
                  const nextFilters = { ...filters };
                  delete nextFilters.dateFrom;
                  delete nextFilters.dateTo;
                  setFilters(nextFilters);
                }
              }}
            />

            <Button icon={<SearchOutlined />} type="primary" onClick={() => refetch()}>
              検索
            </Button>
          </Space>

          <ResponsiveTable
            columns={columns}
            dataSource={logs}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: limit,
              total: data?.total || 0,
              showSizeChanger: false,
              showTotal: (total) => `全 ${total} 件`,
              onChange: (newPage) => setPage(newPage),
            }}
            scrollX={1200}
            mobileCardConfig={{
              titleField: 'createdAt',
            }}
          />
        </Space>
      </Card>
    </PageContainer>
  );
}
